import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FiEdit, FiTrash } from "react-icons/fi";
import { GoPrimitiveDot } from "react-icons/go";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import Swal from "sweetalert2";
import moment from "moment";
import { APP_CONTEXT } from "../../../utils/context";
import PageHeader from "../../../components/PageHeader/PageHeader.component";
import Button from "../../../components/Button/Button.component";
import Table, { TableIcons } from "../../../components/Table/Table.component";
import { DELETE_CMS_ENTRY } from "../../../graphql/mutations/cmsEntry.mutation";
import Notification from "../../../components/Toast/Toast.component";

// GQL
import {
  GET_CONTENT_MANAGEMENT_ALL,
  GET_CONTENT_MANAGEMENT_ALL_EXPORT,
} from "../../../graphql/queries/contentManagement.query";
import { formatGraphqlError } from "../../../utils/api";

const ContentAll = () => {
  const { setActiveSideNav, setPageTitle } = useContext(APP_CONTEXT);
  const [refetch, { data, loading, error }] = useLazyQuery(
    GET_CONTENT_MANAGEMENT_ALL,
    {
      variables: { order: "createdAt_DESC" },
      fetchPolicy: "no-cache",
    }
  );
  const [variables, setVariables] = useState({
    where: { AND: [{ NOT: { status: "ARCHIVED" } }] },
  });
  const [deleteContent] = useMutation(DELETE_CMS_ENTRY);
  const [getContentsExport, cmsEntriesExport] = useLazyQuery(
    GET_CONTENT_MANAGEMENT_ALL_EXPORT
  );

  useEffect(() => {
    setActiveSideNav("cm");
    setPageTitle("Content Management All");
  });

  const handleDelete = (e, row) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.value) {
        deleteContent({
          variables: {
            id: row.original.id,
          },
        })
          .then(() => {
            Swal.fire("Deleted!", "Content has been deleted", "success");
            refetch({
              variables: {
                ...variables,
                first: 50,
                skip: 0,
              },
            });
          })
          .catch((err) =>
            Notification.error({
              title: "Delete Content Failed",
              message: formatGraphqlError(err),
            })
          );
      }
    });
  };

  const getColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "#39C568";
      case "ARCHIVE":
        return "#FF003A";
      case "DRAFT":
        return "#707172";
      default:
        return "#707172";
    }
  };

  const HeaderInfo = [
    {
      Header: "Name",
      accessor: "title",
      id: "title",
      search: true,
    },
    {
      Header: "Content Type",
      accessor: "type",
      id: "type",
      Cell: ({ row }) => row.original.type.toLowerCase(),
    },
    {
      Header: "Publish Date",
      accessor: "publishAt",
      id: "publishAt",
      Cell: ({ row }) => moment(row.original.publishAt).format("YYYY/MM/DD | HH:mm"),
    },
    {
      Header: "Status",
      id: "status",
      Cell: ({ row }) => (
        <TableIcons>
          <GoPrimitiveDot
            style={{ fontSize: "25px", color: getColor(row.original.status) }}
          />
          <span>{row.original.status.toLowerCase()}</span>
        </TableIcons>
      ),
    },
    {
      Header: "Action",
      id: "actions",
      Cell: ({ row }) => (
        <TableIcons>
          <Link to={`/app/cms/content-management/${row.original.id}`}>
            <FiEdit
              style={{ fontSize: "20px", color: "#07002F", cursor: "pointer" }}
            />
          </Link>
          <FiTrash
            onClick={(e) => handleDelete(e, row)}
            style={{ fontSize: "20px", color: "#07002F", cursor: "pointer" }}
          />
        </TableIcons>
      ),
    },
  ];

  const filters = [
    {
      header: "Date Created:",
      accessor: "createdAt",
      type: "daterange",
    },
    {
      header: "Status:",
      accessor: "status",
      placeholder: "Please select a Status",
      type: "select",
      options: [
        { value: "ACTIVE", text: "Active" },
        { value: "ARCHIVE", text: "Archive" },
        { value: "DRAFT", text: "Draft" },
      ],
    },
  ];

  return (
    <>
      <PageHeader title="Content Management">
        <Link to="/app/cms/content-management/new">
          <Button
            onClick={() => {}}
            kind="secondary"
            value="New Content"
            width="177px"
            margin="10px 0 10px 10px"
          />
        </Link>
      </PageHeader>
      <Table
        columns={HeaderInfo}
        data={(data && data.cmsEntries) || []}
        loading={loading}
        error={error}
        refetch={refetch}
        variables={variables}
        dataCount={(data && data.cmsEntryCount.length) || 0}
        pageCount={5}
        setVariables={setVariables}
        tableTitle="Contents"
        tableOptions={["search", "download", "columns", "filter"]}
        filters={filters}
        downloadData={cmsEntriesExport}
        getDownloadData={getContentsExport}
        downloadAccessor={
          cmsEntriesExport.data ? cmsEntriesExport.data.cmsEntries : []
        }
        fileName="cmsEntriesManagement"
      />
    </>
  );
};

export default ContentAll;
